// PERETELE CORTINĂ pe machetă: panoul `curtain_wall` al unui etaj, cu grila
// lui din document (`props["architecture.panel"].curtain`, v. `layout.cortina`):
// montanții, traversele zonei opace de foc, sticla celulelor vizibile, sticla
// emailată a celor opace. Nimic inventat: pozițiile, adâncimile și felul
// fiecărei celule vin din document.
//
// Reperul local, ca la tâmplărie: `x` de la capătul `a` al panoului spre `b`,
// `y` pe normala feței spre afară (0 = planul zidăriei, adică muchia plăcii),
// `z` de la pardoseala etajului.
import * as THREE from "three";
import { Trunchi } from "./tamplarie3d";
import { fundalCamerei, samantaDin } from "./camera3d";

export interface CortinaPanou {
  /** Montanții, de la capătul `a` al panoului, în metri (primul 0, ultimul L). */
  montanti: number[];
  /** Felul fiecărei coloane, pe rândul vizibil: vision | vent | opaque | open | door. */
  celule: string[];
  /** Liniile traverselor zonei opace: [parapet, sub placă], de la pardoseală. */
  randuri: number[];
  varianta: string;
  latime: number;
  montant: number;
  traversa: number;
  capac: number;
  rost: number;
  sticla: { culoare: string; grosime: number };
  opac: { culoare: string };
  /** Cât se prelungește panoul dincolo de capete (colțul ieșind al corpului). */
  capete: [number, number];
  /** Ușile din cortină: `[x0, x1, pragul, capul]` de la începutul panoului. */
  usi?: number[][];
}

/** Cât de lungă poate fi o încăpere închipuită în spatele cortinei. */
const CAMERA_MAX_M = 6.0;

export interface PartiCortina {
  profile: THREE.BufferGeometry[];
  sticla: THREE.BufferGeometry[];
  opac: THREE.BufferGeometry[];
  /** Camerele închipuite din spatele celulelor vizibile (v. `camera3d`). */
  camere: THREE.BufferGeometry[];
}

/** Geometria panoului de cortină de pe segmentul `a`→`b` (normala `n`), cu
 *  fața capacelor la `adancime` de muchia plăcii, de la `z0` la `z1` (pe
 *  nivel, peste `zBaza`). */
export function geometriaCortinei(a: number[], b: number[], n: number[], z0: number, z1: number,
                                  adancime: number, c: CortinaPanou, zBaza = 0): PartiCortina {
  const out: PartiCortina = { profile: [], sticla: [], opac: [], camere: [] };
  const L = Math.hypot(b[0] - a[0], b[1] - a[1]);
  const H = z1 - z0;
  if (L < 0.2 || H < 0.5 || c.montanti.length < 2) return out;
  const u = [(b[0] - a[0]) / L, (b[1] - a[1]) / L];
  const w = c.latime, D = adancime;
  // PLANURILE pe adâncime: montantul de la rost la capac, traversa mai
  // scurtă dinăuntru, sticla sub placa de presare (12 mm) și capac
  const yIn = c.rost, yOut = D;
  const yTr = Math.max(yIn, D - c.capac - c.traversa);
  const yG1 = D - c.capac - 0.012, yG0 = yG1 - c.sticla.grosime;
  // VARIANTA: capacele pe amândouă direcțiile (standard), numai pe traverse
  // (horizontal), numai pe montanți (vertical), niciunul (structural: profilul
  // rămâne în spatele sticlei, cu rostul de 20 mm între geamuri)
  const capV = c.varianta === "standard" || c.varianta === "vertical";
  const capO = c.varianta === "standard" || c.varianta === "horizontal";
  const yMont = capV ? yOut : yG0 - 0.002;
  const yTrav = capO ? yOut : yG0 - 0.002;
  const zb = (z: number) => zBaza + z0 + z;
  const pr = new Trunchi([a[0], a[1]], u, n);
  const gl = new Trunchi([a[0], a[1]], u, n);
  const op = new Trunchi([a[0], a[1]], u, n);
  const m = c.montanti;
  // PESTE PLACĂ (aticul, parapetul terasei: `rows_z` gol) cortina e un singur
  // rând, opac, fără traversele zonei de foc
  const unRand = c.randuri.length < 2;
  const r1 = unRand ? H : Math.min(Math.max(c.randuri[0], 0.1), H - 0.2);
  const r2 = unRand ? H : Math.max(Math.min(c.randuri[1], H - 0.05), r1 + 0.1);
  // rândurile: [jos, sus, zonă vizibilă?]
  const randuri: [number, number, boolean][] = unRand
    ? [[0, H, false]] : [[0, r1, false], [r1, r2, true], [r2, H, false]];
  // GURA LOGIEI e deschisă numai pe rândul vizibil (zona de foc e parapetul ei);
  // COLOANA UȘII, de jos până la capul ușii — deasupra se închide, opacă
  const usaDin = (j: number) => {
    const xm = (m[j] + m[j + 1]) / 2;
    return (c.usi ?? []).find((d) => d[0] - 0.02 <= xm && xm <= d[1] + 0.02);
  };
  const capUsa = (j: number) => { const u = usaDin(j); return u ? u[u.length - 1] : r2; };
  const pragUsa = (j: number) => { const u = usaDin(j); return u && u.length >= 4 ? u[2] : 0; };
  const deschisa = (j: number, vizibil: boolean) =>
    c.celule[j] === "door" || (vizibil && c.celule[j] === "open");
  // MONTANȚII, pe rânduri: unul dintre două coloane deschise (gura unei logii)
  // nu trece prin golul ei — rămâne doar în zona opacă de sus și de jos
  for (let k = 0; k < m.length; k++) {
    const x0 = Math.min(Math.max(m[k] - w / 2, 0), L - w);
    // între două coloane ale aceleiași uși (o ușă lată ia două celule): numai
    // deasupra capului ei — dedesubt montantul ar trece prin fața ușii
    if (k > 0 && k < m.length - 1 && c.celule[k - 1] === "door" && c.celule[k] === "door") {
      const cap = Math.max(capUsa(k - 1), capUsa(k));
      if (cap < H - 0.02) pr.cutie(x0, x0 + w, yIn, yMont, zb(cap), zb(H));
      continue;
    }
    for (const [ra, rb, viz] of randuri) {
      if (viz && k > 0 && k < m.length - 1 && deschisa(k - 1, viz) && deschisa(k, viz)) continue;
      pr.cutie(x0, x0 + w, yIn, yMont, zb(ra), zb(rb));
    }
  }
  // COLȚUL IEȘIND: panoul se prelungește cu adâncimea lui și se închide cu un
  // stâlp de colț, pe toată înălțimea
  // …de la muchia plăcii, ca să acopere și capătul finisajului feței vecine
  if (c.capete[0] > 0.01) pr.cutie(-c.capete[0], 0, 0.002, yOut, zb(0), zb(H));
  if (c.capete[1] > 0.01) pr.cutie(L, L + c.capete[1], 0.002, yOut, zb(0), zb(H));
  // TRAVERSELE: la pardoseală și la placa de sus (jumătate fiecare — cu etajul
  // vecin fac o traversă întreagă la muchia plăcii) și pe liniile zonei opace
  for (let j = 0; j + 1 < m.length; j++) {
    const xa = m[j] + w / 2, xb = m[j + 1] - w / 2;
    if (xb - xa < 0.02) continue;
    const linii: [number, number][] = unRand ? [[0, w / 2], [H - w / 2, H]]
      : [[0, w / 2], [H - w / 2, H], [r1 - w / 2, r1 + w / 2], [r2 - w / 2, r2 + w / 2]];
    // în coloana UȘII, nicio traversă între prag și cap: ar tăia ușa (cea de
    // intrare, pe un bloc ridicat, coboară sub panou, până la teren)
    const usa = c.celule[j] === "door";
    for (const [za, zc] of linii) {
      if (usa && zc > pragUsa(j) + 1e-6 && za < capUsa(j) - 1e-6) continue;
      pr.cutie(xa, xb, yTr, yTrav, zb(za), zb(zc));
    }
    // CELULELE: sticla în rama montanților și a traverselor, cu falțul de
    // 15 mm sub capace (fără capace, un rost de 10 mm între geamuri)
    const falt = capV ? 0.015 : -0.005;
    const fz = capO ? 0.015 : -0.005;
    if (c.celule[j] === "door") {
      // deasupra ușii, până la rândul de sus: opac, pe o traversă la capul ei
      // Traversa de la cap și cea de la prag merg până la rost, pe toată
      // adâncimea montanților: ușa stă mai înăuntru, iar o traversă scurtă
      // lăsa între ea și tocul ușii o fâșie din care se vedea betonul
      const zc = Math.min(capUsa(j), r2 - 0.05);
      pr.cutie(xa, xb, yIn, yTrav, zb(zc), zb(zc + w));
      if (r2 - (zc + w) > 0.02) op.cutie(xa - falt, xb + falt, yG0, yG1, zb(zc + w - fz), zb(r2 - w / 2 + fz));
      // …și sub pragul ei (ușa terasei stă cu 0,30 peste placă)
      const zp = pragUsa(j);
      if (zp > 0.05) {
        op.cutie(xa - falt, xb + falt, yG0, yG1, zb(w / 2 - fz), zb(zp - w));
        pr.cutie(xa, xb, yIn, yTrav, zb(zp - w), zb(zp));
      }
    }
    for (const [ra, rb, viz] of randuri) {
      // coloana ușii: liberă sub rândul de sus (umplutura de deasupra ușii e mai sus);
      // gura logiei: liberă numai pe rândul vizibil
      if (c.celule[j] === "door" && ra < r2 - 1e-6) continue;
      if (c.celule[j] === "open" && viz) continue;
      const zA = (ra === 0 ? w / 2 : ra + w / 2) - fz, zB = (rb === H ? H - w / 2 : rb - w / 2) + fz;
      if (zB - zA < 0.02) continue;
      const tinta = viz && (c.celule[j] === "vision" || c.celule[j] === "vent") ? gl : op;
      tinta.cutie(xa - falt, xb + falt, yG0, yG1, zb(zA), zb(zB));
      // TAVA IZOLATĂ din spatele zonei opace, lângă rost: fără ea, prin rostul
      // dintre geam și traversă (variantele fără capace) se vedea betonul
      if (tinta === op) op.cutie(xa, xb, yIn, yIn + 0.008, zb(ra), zb(rb));
    }
  }
  // CAMERELE din spatele sticlei, la muchia plăcii: una pe fiecare șir de
  // celule vizibile alăturate (camera cu trei geamuri e o singură încăpere),
  // de la pardoseala etajului până sub placa de sus. Un șir e cel mult o
  // cameră (`CAMERA_MAX_M`): un front de 40 m era o singură încăpere, cu
  // aceeași lumină și aceeași perdea pe tot etajul
  const vede = (j: number) => c.celule[j] === "vision" || c.celule[j] === "vent";
  for (let j = 0; j + 1 < m.length; j++) {
    if (!vede(j)) continue;
    let k = j;
    while (k + 1 < m.length - 1 && vede(k + 1) && m[k + 2] - m[j] <= CAMERA_MAX_M) k++;
    const xa = m[j], xb = m[k + 1];
    const cx = a[0] + u[0] * (xa + xb) / 2, cy = a[1] + u[1] * (xa + xb) / 2;
    for (let q = j; q <= k; q++) {
      out.camere.push(fundalCamerei(a, u, n, 0.004, m[q] + w / 2, m[q + 1] - w / 2, zb(r1), zb(r2), zb(0), {
        x0: xa - 0.6, x1: xb + 0.6, adancime: 5.0, inaltime: H - 0.25,
        gol0: xa, gol1: xb, samanta: samantaDin(cx, cy, zb(0)),
      }));
    }
    j = k;
  }
  for (const [lst, tr] of [[out.profile, pr], [out.sticla, gl], [out.opac, op]] as const) {
    const g = tr.geometrie();
    if (g) lst.push(g);
  }
  return out;
}
